/**
 * SEO Utility Functions
 * Schema generators, meta tag builders, and SEO helpers
 */

import {
  siteConfig,
  SiteConfig,
  PageMeta,
  BreadcrumbItem,
  LocationData,
  ServiceData,
  BlogPost,
  FAQItem,
} from '../data/seo';

// ============================================================================
// SCHEMA GENERATORS
// ============================================================================

/**
 * Generate LocalBusiness schema for the main business
 */
export const generateLocalBusinessSchema = (config: SiteConfig = siteConfig) => {
  const sameAs = Object.values(config.social).filter(
    (value): value is string => typeof value === 'string' && /^https?:\/\//i.test(value)
  );

  return {
    '@context': 'https://schema.org',
    '@type': ['VeterinaryCare', 'LocalBusiness', 'MedicalBusiness'],
    '@id': `${config.url}/#business`,
    name: config.name,
    alternateName: `${config.name} Fort Lauderdale`,
    description: 'Professional veterinary dental care specializing in dog and cat dental health. Expert teeth cleaning, oral surgery, and dental treatments for pets in South Florida.',
    url: config.url,
    sameAs,
    logo: `${config.url}/Chomp_Logo_Black.png`,
    image: [
      `${config.url}/homeimagebouviercat.png`,
      `${config.url}/Chomp-Front.png`,
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: config.address.street,
      addressLocality: config.address.city,
      addressRegion: config.address.state,
      postalCode: config.address.zip,
      addressCountry: config.address.country,
    },
    telephone: config.phone,
    email: config.email,
    ...(config.openingHours.length ? { openingHours: config.openingHours } : {}),
    priceRange: config.priceRange,
    paymentAccepted: 'Cash, Credit Card, Pet Insurance, Cherry, CareCredit',
    currenciesAccepted: 'USD',
    serviceType: 'Veterinary Dental Care',
    specialty: [
      'Veterinary Dentistry',
      'Pet Dental Cleaning',
      'Dog Dental Care',
      'Cat Dental Care',
      'Pet Oral Surgery',
      'Dental X-rays for Pets',
    ],
  };
};

/**
 * Generate VeterinaryCare schema for a specific location
 */
export const generateLocationSchema = (location: LocationData, config: SiteConfig = siteConfig) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Pet dental care for ${location.name} residents`,
    description: `Dog and cat dental care for ${location.name} residents at our Fort Lauderdale clinic.`,
    url: `${config.url}/locations/${location.slug}`,
    serviceType: 'Veterinary Dental Care',
    provider: { '@id': `${config.url}/#business` },
    areaServed: { '@type': 'City', name: `${location.name}, ${location.state}` },
  };
};

/**
 * Generate Service schema
 */
export const generateServiceSchema = (service: ServiceData, config: SiteConfig = siteConfig) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.longDescription,
    provider: {
      '@type': 'VeterinaryCare',
      '@id': `${config.url}/#business`,
      name: config.name,
      url: config.url,
    },
    serviceType: 'Veterinary Dental Care',
    areaServed: {
      '@type': 'State',
      name: 'Florida',
    },
    offers: {
      '@type': 'Offer',
      price: service.priceValue,
      priceCurrency: 'USD',
    },
  };
};

/**
 * Generate MedicalProcedure schema for a dental procedure offered to pets.
 * Describes the procedure; this markup does not guarantee search visibility.
 *
 * https://schema.org/MedicalProcedure
 */
export const generateMedicalProcedureSchema = (
  service: ServiceData,
  config: SiteConfig = siteConfig
) => {
  const speciesText = service.petTypes
    .map((p) => (p === 'dog' ? 'dogs' : 'cats'))
    .join(' and ');
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: service.name,
    alternateName: `Veterinary ${service.name}`,
    description: service.longDescription,
    procedureType: 'https://schema.org/SurgicalProcedure',
    bodyLocation: 'Mouth, teeth, gums, and oral cavity',
    preparation:
      "Follow the veterinary team's individualized preparation instructions, including any required assessment and bloodwork.",
    followup:
      `Recovery instructions, take-home pain medication if needed, and a complimentary recheck two to three weeks post-procedure. For ${speciesText}.`,
    howPerformed:
      'Performed under modern veterinary general anesthesia with IV catheter, fluid therapy, and continuous vital monitoring. Includes ultrasonic scaling, full-mouth dental X-rays, charting, polishing, and any indicated periodontal treatment or extractions.',
    relevantSpecialty: {
      '@type': 'MedicalSpecialty',
      name: 'Veterinary Dentistry',
    },
    performer: {
      '@type': 'VeterinaryCare',
      '@id': `${config.url}/#business`,
      name: config.name,
      url: config.url,
    },
    offers: {
      '@type': 'Offer',
      price: service.priceValue,
      priceCurrency: 'USD',
      url: `${config.url}/services/${service.slug}`,
      availability: 'https://schema.org/InStock',
    },
  };
};

/**
 * Generate Article schema for blog posts
 */
export const generateArticleSchema = (post: BlogPost, config: SiteConfig = siteConfig) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    image: post.image ? `${config.url}${post.image}` : `${config.url}/homeimagebouviercat.png`,
    author: {
      '@type': 'Organization',
      name: post.author || config.name,
      url: config.url,
    },
    publisher: {
      '@type': 'Organization',
      name: config.name,
      url: config.url,
      logo: {
        '@type': 'ImageObject',
        url: `${config.url}/Chomp_Logo_Black.png`,
      },
    },
    datePublished: post.datePublished,
    dateModified: post.dateModified,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${config.url}/blog/${post.slug}`,
    },
    keywords: post.tags.join(', '),
  };
};

/**
 * Generate FAQPage schema
 */
export const generateFAQSchema = (faqs: FAQItem[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    // SpeakableSpecification tells voice assistants which DOM regions to read
    // aloud. We target FAQ section headings and paragraphs by selector so this
    // works on any page that follows the standard FAQ markup.
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['#faq h2', '#faq h3', '#faq p', '[data-speakable]'],
    },
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/\n/g, ' ').replace(/•/g, '-'),
      },
    })),
  };
};

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items: BreadcrumbItem[], config: SiteConfig = siteConfig) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${config.url}${item.url}`,
    })),
  };
};

/**
 * Generate aggregate offer schema for services
 */
export const generateAggregateOfferSchema = (services: ServiceData[], config: SiteConfig = siteConfig) => {
  const prices = services.map(s => s.priceValue);
  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateOffer',
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    priceCurrency: 'USD',
    offerCount: services.length,
    offers: services.map(service => ({
      '@type': 'Offer',
      name: service.name,
      description: service.shortDescription,
      price: service.priceValue,
      priceCurrency: 'USD',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
      },
    })),
  };
};

// ============================================================================
// META TAG HELPERS
// ============================================================================

/**
 * Generate page title with site name suffix
 */
export const generatePageTitle = (pageTitle: string, includeSiteName: boolean = true): string => {
  if (!includeSiteName) return pageTitle;
  if (pageTitle.includes('CHOMP')) return pageTitle;
  return `${pageTitle} | ${siteConfig.name}`;
};

/**
 * Generate canonical URL
 */
export const generateCanonicalUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${siteConfig.url}${cleanPath}`;
};

/**
 * Generate Open Graph meta tags
 */
export const generateOpenGraphMeta = (meta: PageMeta) => {
  return {
    'og:type': meta.ogType || 'website',
    'og:title': meta.title,
    'og:description': meta.description,
    'og:url': meta.canonical,
    'og:image': meta.ogImage || `${siteConfig.url}/homeimagebouviercat.png`,
    'og:site_name': siteConfig.name,
    'og:locale': 'en_US',
  };
};

/**
 * Generate Twitter Card meta tags
 */
export const generateTwitterMeta = (meta: PageMeta) => {
  return {
    'twitter:card': 'summary_large_image',
    'twitter:site': '@chomppetdental',
    'twitter:title': meta.title,
    'twitter:description': meta.description,
    'twitter:image': meta.ogImage || `${siteConfig.url}/homeimagebouviercat.png`,
  };
};

// ============================================================================
// LOCATION PAGE SEO HELPERS
// ============================================================================

/**
 * Generate location page meta
 */
export const generateLocationMeta = (location: LocationData): PageMeta => {
  return {
    title: `Pet Dental Care for ${location.name}, FL | CHOMP`,
    description: `Dog and cat dental care for ${location.name} residents at CHOMP in Fort Lauderdale. Visit our clinic at 1800 E Commercial Blvd. See pricing and request an appointment.`,
    canonical: generateCanonicalUrl(`/locations/${location.slug}`),
    keywords: [
      `pet dentist ${location.name}`,
      `dog dentist ${location.name}`,
      `cat teeth cleaning ${location.name}`,
      `veterinary dental ${location.name}`,
      `${location.name} pet dental`,
    ],
    ogImage: `${siteConfig.url}/Chomp-Front.png`,
  };
};

/**
 * Generate blog post meta
 */
export const generateBlogPostMeta = (post: BlogPost): PageMeta => {
  return {
    title: `${post.title} | CHOMP Pet Dental Blog`,
    description: post.metaDescription,
    canonical: generateCanonicalUrl(`/blog/${post.slug}`),
    keywords: post.tags,
    ogImage: post.image ? `${siteConfig.url}${post.image}` : `${siteConfig.url}/homeimagebouviercat.png`,
    ogType: 'article',
  };
};

/**
 * Generate service page meta for programmatic SEO
 */
export const generateServiceLocationMeta = (
  service: ServiceData,
  location: LocationData
): PageMeta => {
  const petType = service.petTypes.includes('dog') ? 'Dog' : 'Cat';
  return {
    title: `${service.name} for ${petType}s in ${location.name}, FL | CHOMP Pet Dental`,
    description: `Professional ${service.name.toLowerCase()} for ${petType.toLowerCase()}s in ${location.name}. ${service.shortDescription}. Serving ${location.neighborhoods.slice(0, 2).join(' & ')} areas. Book today!`,
    canonical: generateCanonicalUrl(`/services/${service.slug}/${location.slug}`),
    keywords: [
      ...service.keywords,
      `${location.name} ${petType.toLowerCase()} dental`,
      `${service.name.toLowerCase()} ${location.name}`,
    ],
  };
};

// ============================================================================
// INTERNAL LINKING HELPERS
// ============================================================================

/**
 * Get related locations for internal linking
 */
export const getRelatedLocations = (currentLocation: LocationData, allLocations: LocationData[], limit: number = 3): LocationData[] => {
  // Priority: nearby locations, then same county, then random
  const nearby = allLocations.filter(loc => 
    currentLocation.nearbyLocations.includes(loc.slug) && loc.slug !== currentLocation.slug
  );
  
  const sameCounty = allLocations.filter(loc => 
    loc.county === currentLocation.county && 
    loc.slug !== currentLocation.slug &&
    !nearby.includes(loc)
  );
  
  const combined = [...nearby, ...sameCounty];
  return combined.slice(0, limit);
};

/**
 * Get related blog posts for internal linking
 */
export const getRelatedPosts = (currentPost: BlogPost, allPosts: BlogPost[], limit: number = 3): BlogPost[] => {
  // Priority: same category, then matching tags
  const sameCategory = allPosts.filter(post => 
    post.category === currentPost.category && post.slug !== currentPost.slug
  );
  
  const matchingTags = allPosts.filter(post => 
    post.slug !== currentPost.slug &&
    !sameCategory.includes(post) &&
    post.tags.some(tag => currentPost.tags.includes(tag))
  );
  
  const combined = [...sameCategory, ...matchingTags];
  return combined.slice(0, limit);
};

// ============================================================================
// SITEMAP HELPERS
// ============================================================================

interface SitemapEntry {
  loc: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: number;
}

/**
 * Generate all sitemap entries
 */
export const generateSitemapEntries = (
  locations: LocationData[],
  blogPosts: BlogPost[],
  services: ServiceData[]
): SitemapEntry[] => {
  const today = new Date().toISOString().split('T')[0];
  
  const entries: SitemapEntry[] = [
    // Core pages
    { loc: '/', lastmod: today, changefreq: 'weekly', priority: 1.0 },
    { loc: '/services', lastmod: today, changefreq: 'monthly', priority: 0.9 },
    { loc: '/book', lastmod: today, changefreq: 'weekly', priority: 0.9 },
    { loc: '/research', lastmod: today, changefreq: 'monthly', priority: 0.7 },
    { loc: '/team', lastmod: today, changefreq: 'monthly', priority: 0.6 },
    { loc: '/contact', lastmod: today, changefreq: 'monthly', priority: 0.8 },
    { loc: '/referrals', lastmod: today, changefreq: 'monthly', priority: 0.7 },
    { loc: '/blog', lastmod: today, changefreq: 'weekly', priority: 0.8 },
    { loc: '/south-florida-pet-dental', lastmod: today, changefreq: 'monthly', priority: 0.8 },
    
    // Location pages
    ...locations.map(loc => ({
      loc: `/locations/${loc.slug}`,
      lastmod: today,
      changefreq: 'monthly' as const,
      priority: loc.county === 'Broward' ? 0.9 : 0.8,
    })),
    
    // Blog posts
    ...blogPosts.map(post => ({
      loc: `/blog/${post.slug}`,
      lastmod: post.dateModified,
      changefreq: 'monthly' as const,
      priority: post.featured ? 0.8 : 0.7,
    })),
    
    // Service pages (if implemented)
    ...services.map(svc => ({
      loc: `/services/${svc.slug}`,
      lastmod: today,
      changefreq: 'monthly' as const,
      priority: 0.8,
    })),
  ];
  
  return entries;
};

/**
 * Generate sitemap XML content
 */
export const generateSitemapXML = (entries: SitemapEntry[]): string => {
  const urlEntries = entries.map(entry => `
  <url>
    <loc>${siteConfig.url}${entry.loc}</loc>
    <lastmod>${entry.lastmod}</lastmod>
    <changefreq>${entry.changefreq}</changefreq>
    <priority>${entry.priority}</priority>
  </url>`).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:xhtml="http://www.w3.org/1999/xhtml"
        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">
${urlEntries}
</urlset>`;
};



